import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPurchase, resetPayment } from 'actions/purchase';
import { clearError } from '@/actions';
import LoadingScreen from 'components/LoadingScreen';
import NotFound from 'components/NotFound';
import { hasVoucherForEngine } from './voucherRegistry';
import PaymentFormat from './PaymentFormat';

/**
 * Notification codes `actions/purchase` dispatches when the fetches this page depends on fail.
 * `getPurchase` swallows both rejections (it only dispatches `setError` and logs), so the store's
 * error code is the only signal a fetch failed — without it `loaded` stays false forever and this
 * page would spin indefinitely.
 */
const PURCHASE_FETCH_ERROR_CODE = 201;
const PAYMENT_FETCH_ERROR_CODE = 304;

/**
 * PaymentFormatContainer.
 * Fetches the purchase for the `:token` route param and its last payment, then renders the
 * printable store-payment voucher (ficha) for the payment engine. This replaces the
 * frontline-platform `GET /payment_format/:id` page: the tenant/brand is resolved from the URL
 * subdomain, so no `tenant` query param is required.
 *
 * Guards (parity with frontline's two `RoutingError` checks, plus the failure paths frontline got
 * for free by rendering server-side):
 * - while the purchase/payment are loading, a LoadingScreen is shown;
 * - when either fetch fails, the purchase has no payment at all, the last payment has no
 *   `reference`, or the engine has no registered voucher, the funnel NotFound (404) is rendered
 *   instead of a partial or stale voucher.
 *
 * `NotFound` navigates with `history.push`, and it is rendered here as a child rather than by a
 * `Route`, so the router history is passed explicitly.
 *
 * @returns {JSX.Element} The loading screen, the voucher, or NotFound.
 */
const PaymentFormatContainer = () => {
  const { token } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const purchaseLoaded = useSelector((state) => state.purchase.get('loaded'));
  const lastPaymentId = useSelector((state) => state.purchase.get('lastPaymentId'));
  const paymentLoaded = useSelector((state) => state.payment.get('loaded'));
  const paymentFetching = useSelector((state) => state.payment.get('isFetching'));

  const reference = useSelector((state) => state.payment.get('reference'));
  const paymentEngine = useSelector((state) => state.payment.get('paymentEngine'));
  const errorCode = useSelector((state) => state.errors.code);

  useEffect(() => {
    // The payment slice is shared across the funnel, so clear it (and any error left from an
    // earlier screen) before fetching: otherwise a stale reference/engine could be rendered as
    // this token's voucher, or a stale error code could 404 a healthy page.
    dispatch(clearError());
    dispatch(resetPayment());
    dispatch(getPurchase(token));
  }, [dispatch, token]);

  const fetchFailed =
    errorCode === PURCHASE_FETCH_ERROR_CODE || errorCode === PAYMENT_FETCH_ERROR_CODE;
  if (fetchFailed) {
    return <NotFound history={history} />;
  }

  // The purchase is still loading, or it has a payment we are still fetching.
  const waitingForPayment = purchaseLoaded && lastPaymentId && (paymentFetching || !paymentLoaded);
  if (!purchaseLoaded || waitingForPayment) {
    return <LoadingScreen hasHeader={false} />;
  }

  // Parity with frontline: no payment, no reference, or no template for the engine → Not Found.
  if (!lastPaymentId || !reference || !hasVoucherForEngine(paymentEngine)) {
    return <NotFound history={history} />;
  }

  return <PaymentFormat />;
};

export default PaymentFormatContainer;
